<script setup lang="ts">
import { CheckboxGroupValueType } from "element-plus";
import { computed, onMounted, ref, watch } from "vue";
import { CalcInputs, HousingSiteDetail } from "~/api";
import { useUnitTypeTree } from "~/composables/useUnitTypeTree";
import { showError } from "~/utils/handleAPIError";
import { formatNumber } from "~/utils/miscUtils";
import { useHousingSiteDetailStore } from "../../stores/HousingSiteDetailStore";
import { ResultPlus } from "../types/plusTypes";

const props = defineProps<{
  result: ResultPlus;
  inputs: CalcInputs;
  checkFavorite: boolean;
  applicationInProgress: boolean;
  collapsed: boolean;
  isReadOnly: boolean;
}>();

const housingSiteDetailStore = useHousingSiteDetailStore();
const { getLeafGuidByName, loadUnitTypeTree } = useUnitTypeTree();

const emit = defineEmits<{
  (
    e: "click:favorite",
    favorite: boolean,
    siteCode: string,
    inBatch: boolean,
  ): void;
  (
    e: "click:option",
    selectedOptions: CheckboxGroupValueType,
    siteCode: string,
  ): void;
}>();

const favorite = ref<boolean>();
const applicationInProgress = props.applicationInProgress;

// watch for changes in checkFavorite and update favorite accordingly
watch(
  () => props.checkFavorite,
  (newValue) => {
    favorite.value = newValue;
  },
  { immediate: true },
);

// const isSupportiveLiving = computed((): boolean => {
//   return (
//     props.result.site?.facilityType === HousingFacilityType.SUPPORTIVE_LIVING
//   );
// });

const hasStudioRooms = computed((): boolean => {
  const studioGuid = getLeafGuidByName("Studio");
  if (studioGuid) {
    return (
      props.result.eligibleUnitTypeLeafNodeGuids?.includes(studioGuid) ?? false
    );
  }
  // Fallback for orgs without a UnitType tree configured.
  return (
    props.result.eligibleUnits?.some((u) => u.toLowerCase() === "s") ?? false
  );
});

const totalRent = computed(() => {
  let total =
    (props.result.servicePackage ?? 0) +
    (props.result.baseRent ?? 0) -
    (props.result.discount ?? 0);

  // iterate through featuresAddedToRent and add the cost to the total
  featuresAddedToRent.value?.forEach((f) => {
    total += f.cost ?? 0;
  });

  detailsData.value?.filter((d) => {
    if (optionsSelected.value?.includes(d.code ?? "")) {
      total += d.cost ?? 0;
    }
  });

  return total;
});

// const optionsTotal = computed(() => {
//   let total = 0;
//   // var total = props.result.finalRent ?? 0;

//   detailsData.value?.filter((d) => {
//     if (optionsSelected.value?.includes(d.code ?? "")) {
//       total += d.cost ?? 0;
//     }
//   });

//   return total;
// });

const otCount = computed(() => {
  let total = 0;
  // var total = props.result.finalRent ?? 0;

  detailsData.value?.filter((d) => {
    if (optionsSelected.value?.includes(d.code ?? "")) {
      total++;
    }
  });

  return total;
});

const remaining = computed(() => {
  let remaining = props.result.remaining ?? 0;

  detailsData.value?.filter((d) => {
    if (optionsSelected.value?.includes(d.code ?? "")) {
      remaining -= d.cost ?? 0;
    }
  });

  return remaining;
});
// get the details data from the server
const detailsData = ref<HousingSiteDetail[]>();
const getDetails = () => {
  const detailCodes = props.result.site?.detailGuids;

  // go through allDetails and get the ones that are in detailCodes
  const local = housingSiteDetailStore.List.filter((d) =>
    detailCodes?.includes(d.guid),
  );

  detailsData.value = local;
};

const options = ref<HousingSiteDetail[]>([]);
const optionsSelected = ref<CheckboxGroupValueType>();
// const optionsSelected = ref<(string | null | undefined)[]>();
const getOptionsForDisplay = () => {
  // go through detailsData.value and get the ones that are optional and sort them by displayOrder
  options.value =
    detailsData.value
      ?.filter((d) => d.isOptional)
      .filter((d) =>
        d.code.startsWith("Studio") ? hasStudioRooms.value : true,
      )
      .sort((a, b) => (a.displayOrder ?? 10000) - (b.displayOrder ?? 10000)) ??
    [];
};

const features = ref<HousingSiteDetail[]>([]);
const getFeaturesForDisplay = () => {
  // go through detailsData.value and get the ones that are not optional
  features.value =
    detailsData.value
      ?.filter(
        (d) => d.isOptional !== true,
        // &&
        // (d.cost === undefined || d.cost === null || d.cost === 0)
      )
      .sort(
        (a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity),
      ) ?? [];
};

const featuresAddedToRent = ref<HousingSiteDetail[] | undefined>();
const getFeaturesIncInRentForDisplay = () => {
  // go through detailsData.value and get the ones that are not optional and have a cost
  featuresAddedToRent.value = detailsData.value?.filter(
    (d) =>
      d.isOptional !== true &&
      d.cost !== undefined &&
      d.cost !== null &&
      d.cost !== 0,
  );
};

onMounted(async () => {
  // loadUnitTypeTree is cached at module scope, so calling it here is a no-op
  // after Finder.vue's initial load — we await it to guarantee flatNodeMap is
  // populated before getOptionsForDisplay reads hasStudioRooms.
  await Promise.all([housingSiteDetailStore.load(), loadUnitTypeTree()]);

  getDetails();
  getOptionsForDisplay();
  getFeaturesForDisplay();
  getFeaturesIncInRentForDisplay();

  favorite.value = props.checkFavorite;
});

const openInNewTab = () => {
  window.open(props.result.site?.detailUrl ?? "", "_blank");
};

// const getQuadrantShortForm = (quadrant: string | null | undefined) => {
//   switch (quadrant) {
//     case "Northwest":
//       return "NW";
//     case "Northeast":
//       return "NE";
//     case "Southwest":
//       return "SW";
//     case "Southeast":
//       return "SE";
//     default:
//       return quadrant;
//   }
// };
const handleClickFavorite = () => {
  if (!props.result.site?.code) {
    showError("Developer Error: tried to favorite a site with no code");
    return;
  }
  emit(
    "click:favorite",
    favorite.value ?? false,
    props.result.site.code,
    false,
  );
};

const handleClickOption = () => {
  if (!props.result.site?.code) {
    showError(
      "Developer Error: tried to click an option for a site with no code",
    );
    return;
  }

  if (!optionsSelected.value) {
    showError(
      "Developer Error: tried to click an option when options was undefined",
    );
    return;
  }

  emit("click:option", optionsSelected.value, props.result.site.code);
};

const expandFeatures = ref(false);
</script>
<template>
  <div class="location">
    <div class="location-header">
      <h3>{{ props.result.hmbName }}</h3>
      <h2 class="location-title">
        {{ props.result.site?.name }}
      </h2>

      <el-checkbox
        v-if="applicationInProgress"
        v-model="favorite"
        class="favorite hidden-on-print"
        :class="{ favorited: favorite }"
        label="This is a favorite!"
        title="Click to add this to your application"
        :disabled="isReadOnly"
        @change="handleClickFavorite"
      >
        <span> Select </span>
      </el-checkbox>
    </div>

    <div v-if="!collapsed" class="location-facility-type">
      <!-- <h3>
        <span v-if="isSupportiveLiving">Supportive Living</span>
        <span v-else>Independent Living</span>
      </h3> -->

      <div
        class="location-facility-features"
        :class="{ expand: expandFeatures }"
        @click="expandFeatures = !expandFeatures"
      >
        <div
          :class="{ expand: expandFeatures, featuresIcons: true }"
          :title="
            expandFeatures ? 'Click to collapse' : 'Click to view descriptions'
          "
        >
          <span
            v-if="features !== undefined && features?.length > 0"
            v-show="expandFeatures"
            class="optionsTitleInHeader"
            >Included</span
          >
          <div v-for="f in features" :key="f.guid" class="withIcon">
            <i v-if="f.icon !== null" :class="`mdi mdi-${f.icon}`"></i>
            <i v-else class="mdi mdi-help-rhombus-outline"></i>
            <span
              :class="{ expand: expandFeatures, collapse: !expandFeatures }"
            >
              {{ f.description }}
              <span v-if="f.comment">({{ f.comment }})</span>
            </span>
          </div>

          <span
            v-if="
              props.result.rentType === 'MKT' &&
              options !== undefined &&
              options?.length > 0
            "
            v-show="expandFeatures"
            class="optionsTitleInHeader"
            >Available</span
          >
          <template
            v-if="props.result.rentType === 'MKT' && options.length > 0"
          >
            <div v-for="o in options" :key="o.guid" class="withIcon options">
              <i v-if="o.icon !== null" :class="`mdi mdi-${o.icon}`"></i>
              <i v-else class="mdi mdi-help-rhombus-outline"></i>
              <span
                :class="{ expand: expandFeatures, collapse: !expandFeatures }"
              >
                {{ o.description }}
                <span v-if="o.comment">({{ o.comment }})</span>
                <span v-if="o.cost"> - ${{ o.cost }}</span>
              </span>
            </div>
          </template>
        </div>
        <!-- <div
          v-if="
            props.result.rentType === 'MKT' &&
            options !== undefined &&
            options?.length > 0
          "
          :class="{ expand: expandFeatures, featuresIcons: true }"
          :title="
            expandFeatures ? 'Click to collapse' : 'Click to view descriptions'
          "
        ></div> -->
      </div>

      <div class="neighborhood-quadrant">
        <span class="neighbourhood">{{
          props.result.site?.location?.trim()
        }}</span>
        <span
          v-if="props.result.site?.location !== props.result.site?.quadrant"
          class="quadrant"
          >{{ props.result.site?.quadrant }}</span
        >
      </div>
    </div>

    <div v-if="!collapsed" class="info location-list-item__excerpt">
      <img
        v-if="props.result.site?.photoUrl"
        class="location-image"
        :src="props.result.site?.photoUrl"
        alt="location image"
      />
      <div class="description">
        <!-- eslint-disable-next-line vue/no-v-html -- TODO: fix in #241 -->
        <div v-html="props.result.site?.description" />
        <el-link
          v-if="props.result.site?.detailUrl"
          type="info"
          class="hidden-on-print"
          @click="openInNewTab"
        >
          See More Details <i :class="`mdi mdi-open-in-new`"></i>
        </el-link>
      </div>
      <div
        v-if="props.result.rentType === 'MKT'"
        class="packageInfo mktHousing"
      >
        {{ props.result.rentMessage }}
      </div>
      <div v-else class="packageInfo">
        <div class="a">
          <span v-if="props.inputs.isDouble === true"
            >Combined base monthly rent
          </span>
          <span v-if="!props.inputs.isDouble">Base monthly rent </span>

          <!-- q: for IL, server is sending the same value for baseRent/rentPlus/finalRent 
                - is it okay to use rentPlus?
           -->
          <span class="number"
            >${{ formatNumber(props.result.baseRent) }}
          </span>
        </div>

        <!-- <div class="supportiveLiving" v-if="isSupportiveLiving"> -->
        <div v-if="props.result.servicePackage" class="b">
          <span>Community service package</span>
          <span class="d number">
            ${{ formatNumber(props.result.servicePackage) }}</span
          >
        </div>
        <div v-if="props.result.discount" class="a">
          <!-- <div class="a"> -->
          <span>Discount * </span>

          <span> - ${{ formatNumber(props.result.discount) }} </span>
        </div>
        <div
          v-if="
            featuresAddedToRent !== undefined && featuresAddedToRent?.length > 0
          "
        >
          <li
            v-for="f in featuresAddedToRent"
            :key="f.guid"
            class="nonOptional"
          >
            <!-- <i :class="`mdi mdi-${f.icon}`"></i> -->
            <span>
              {{ f.description }}
              <span v-if="f.comment">({{ f.comment }})</span>
            </span>
            <span v-if="f.cost">${{ formatNumber(f.cost) }}</span>
          </li>
        </div>

        <div v-if="options.length" class="c">
          <span>Options ({{ otCount }} selected)</span>
          <!-- <span class="number"
            >${{ formatNumber(optionsTotal, true, true) }}
          </span> -->
        </div>
        <!-- <span v-if="props.result.discount"> (includes a discount of ${{ props.result.discount }} )</span> -->
      </div>

      <div
        v-if="props.result.rentType !== 'MKT'"
        class="options"
        :class="{ mktHousing: props.result.rentType === 'MKT' }"
      >
        <el-checkbox-group
          v-model="optionsSelected"
          :disabled="props.result.rentType === 'MKT'"
          @change="handleClickOption"
        >
          <el-checkbox
            v-for="o in options"
            :key="o.guid"
            :label="o.code"
            :value="o.code"
          >
            <div class="optionsList">
              <div class="withIcon">
                <!-- one of these icons will replace the original checkbox when in print mode  -->
                <!-- 'checked' state of original checkbox wasnt showing on print
                      unless background graphics weren't enabled -->
                <i
                  v-if="optionsSelected?.includes(o.code)"
                  class="mdi mdi-checkbox-marked-outline show-on-print"
                />
                <i
                  v-else
                  class="mdi mdi-checkbox-blank-outline show-on-print"
                />
                <!-- icon for the option -->
                <i v-if="o.icon !== null" :class="`mdi mdi-${o.icon}`"></i>
                <i v-else class="mdi mdi-help-rhombus-outline"></i>
                <div>
                  {{ o.description }}
                  <span v-if="o.comment" class="comment">
                    ({{ o.comment }})
                  </span>
                </div>
              </div>
              <div class="cost number">${{ formatNumber(o.cost) }}</div>
            </div>
          </el-checkbox>
        </el-checkbox-group>
      </div>

      <!-- </div> -->
      <div v-if="props.result.rentType !== 'MKT'" class="rent">
        <span> Total monthly payment </span>
        <span class="overline number"> ${{ formatNumber(totalRent) }}</span>
      </div>
      <div
        v-if="props.result.servicePackage"
        class="remaining"
        :class="{ red: remaining < 0 }"
      >
        <p>
          You will have
          <strong class="number">${{ formatNumber(remaining, -2) }}</strong>
          remaining after your total monthly payment.
        </p>
        <!-- <span class="dollar"> {{ formatNumber(remaining, -2) }}</span> -->
      </div>
    </div>

    <div v-if="!collapsed" class="disclaimer">
      <p v-if="props.result.discount">
        * Discount applied to maintain government mandated $370 residual income
        after rent and service package.
      </p>

      <i>
        Disclaimer: This is an estimate only. All figures need to be verified by
        a representative.
      </i>
    </div>
  </div>
</template>

<style lang="less">
.location {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    "headerSection"
    "descriptionSection"
    "infoSection"
    "disclaimer";
  grid-gap: 0;
  /* box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px; */
  margin: 0 0 1em;
  padding: 1em 1em 1em 1em;
  border-radius: 5px;
  border: 1px solid rgba(99, 99, 99, 0.2);
  background: var(--yych-background-color-light);

  h3 {
    margin: 0;
  }

  .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: green;
  }

  .el-checkbox__inner {
    width: 18px;
    height: 18px;
  }

  .el-checkbox__inner::after {
    left: 5px;
    top: 2px;
  }
  // this is to standardize the font size of all list items
  li {
    font-size: 1em;
  }

  .withIcon {
    display: flex;
    align-items: center;
    gap: 0.5em;

    i {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1.25em;
      color: var(--silveraColorSecondary);
    }

    .comment {
      display: block;
      margin-top: 5px;
      font-size: 80%;
      text-wrap: wrap;
      white-space: normal;
      color: #5c5a5a;
    }
  }

  // this applies to the favorite checkbox and the upgrade options
  .el-checkbox__label {
    text-wrap: wrap;
    white-space: normal;
    font-weight: normal;
    font-size: 1em;
  }

  // location header contains the name and the favorite button
  .location-header {
    grid-area: headerSection;
    display: grid;
    grid-template-areas:
      "hmb hmb"
      "name select";
    align-items: start;
    width: 100%;
    margin-bottom: 0.2em;
    place-content: space-between;

    .location-title {
      margin: 0 0.5em 0 0;
      // font-size: 1.7em;
      grid-area: name;
      color: var(--el-text-color-primary);
    }

    .favorite {
      font-size: 1.2em;

      .el-icon {
        font-size: 1.5em;
      }
    }
  }

  .location-facility-type {
    grid-area: descriptionSection;
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 0.5em;
    margin-bottom: 1em;
    color: var(--el-color-info);

    .neighborhood-quadrant {
      align-self: baseline;
      text-align: right;
      .neighbourhood,
      .quadrant {
        font-size: 1.25em;
        font-weight: 500;
      }
      .expand {
        .neighbourhood {
          margin-left: 0;
        }
        .quadrant {
          &:before {
            content: none;
          }
        }
      }
      .quadrant {
        &:before {
          content: ", ";
        }
      }
    }

    .location-facility-features {
      display: flex;
      // flex-direction: column;
      flex-wrap: wrap;
      gap: 5px;
      font-size: 1em;
      cursor: pointer;
      // order: 2; //switch order with neighborhood-quadrant on expand

      .featuresIcons {
        display: flex;
        gap: 5px;

        // when expanded, show the features in a column with the icon description and comment
        &.expand {
          gap: 0px;
          flex-direction: column;
        }

        // when collapsed, show just the icon
        .collapse {
          display: none;
        }

        .options {
          i {
            color: #687783;
          }
        }
      }
    }
  }

  .info {
    grid-area: infoSection;
    margin-top: -0.3em;
    display: grid;
    grid-template-columns: 1fr 5fr 1fr;
    grid-template-areas:
      "img  img img"
      "desc desc desc "
      "x packageInfo y "
      "x options y"
      "x rent y"
      "x remaining y";

    @imgWidth: 100%;

    .location-image {
      grid-area: img;
      object-fit: cover;
      width: 100%;
      max-width: @imgWidth;
      max-height: 500px;
      min-height: 300px;
      border-radius: 5px;
    }

    .description {
      grid-area: desc;
      margin: 1em 0;
      text-align: justify;
      font-size: 1em;
      color: black;

      .el-link {
        margin: 0.5em 0;
        font-weight: 400;

        i {
          margin-left: 0.25em;
        }
      }
    }

    .packageInfo {
      grid-area: packageInfo;
      display: grid;
      // grid-gap: 3px 1.5em;
      grid-template-areas: "a" "b" "c";
      // margin: 1em 0 1em 0;
      color: black;

      .comment {
        font-size: 80%;
        text-wrap: wrap;
        white-space: normal;
        color: #5c5a5a;
      }

      & .a,
      & .b,
      & .c {
        display: grid;
        grid-template-columns: max-content auto;
        justify-content: space-between;
        text-wrap: wrap;
        white-space: normal;
      }

      // .independent-living.a {
      //   // width: 70%;
      // }

      .nonOptional {
        display: flex;
        justify-content: space-between;
      }

      &.mktHousing {
        text-align: center;
      }
    }

    .options {
      grid-area: options;
      font-size: 1em;
      margin: 0px auto 15px 0;
      // border: 1px solid #d5d5d5;
      // box-shadow: rgba(99, 99, 99, 0.2) 2px 3px 0px 0px;
      // box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
      // box-shadow: rgb(191 191 191 / 20%) 6px 6px 0px 0px;
      border-radius: 5px;
      padding: 10px 0px;
      width: 100%;

      .el-checkbox-group {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-left: 10px;
      }

      .el-checkbox {
        display: grid;
        align-items: center;
        height: fit-content;
        grid-template-columns: max-content 1fr;
        margin: 0;
      }

      &.mktHousing {
        margin: 0;
        justify-self: center;
        width: 75%;

        .optionsList {
          width: 100%;
        }

        .el-checkbox {
          grid-template-columns: auto;
          cursor: default;

          .el-checkbox__input.is-disabled + span.el-checkbox__label {
            color: #5c5a5a;
            cursor: default;
          }
        }
      }

      .el-checkbox__input.is-checked + .el-checkbox__label {
        color: black;
      }

      h4 {
        margin: 0 0 10px;
        font-weight: 500;
      }

      .is-checked {
        .optionsList {
          width: 100%;
        }
      }

      .optionsList {
        display: grid;
        font-size: 1em;
        grid-template-columns: auto auto;
        // grid-template-areas: "icon opt cost";
        justify-content: space-between;
        gap: 1em;
        align-items: center;
        width: 75%;
        .withIcon {
          display: flex;
          // align-items: start;
          i {
            // color: var(--silveraColorSecondary);
            color: #687783;

            &.show-on-print {
              display: none;
            }
          }
          div {
            align-self: center;
          }
        }

        //background: #f5ffff;
        // width: 100%;
      }
    }

    .packageInfo,
    .rent,
    .remaining {
      font-size: 1em;
    }

    .rent,
    .remaining {
      display: flex;
      justify-content: space-between;
      margin-top: 10px;
    }

    .rent {
      grid-area: rent;
      color: black;
      font-weight: 500;
    }

    .remaining {
      grid-area: remaining;
      font-weight: normal;
      text-align: center;
    }

    // remaining turns red if it's negative
    .red {
      color: var(--el-color-danger-dark-2);
    }
  }

  .disclaimer {
    grid-area: disclaimer;
    margin-top: 1em;
    text-align: center;
    color: var(--el-color-info);
    p {
      font-size: 1em;
      margin: 8px 0;
    }
  }

  // below 660px, .info grid only has 1 column
  @media (max-width: 660px) {
    .info {
      grid-template-columns: 1fr;
      grid-template-areas:
        "img"
        "desc"
        "packageInfo"
        "options"
        "rent"
        "remaining";
      padding: 0;
    }
  }

  // decreasing font size for smaller screens
  @media (min-width: 500px) and (max-width: 660px) {
    font-size: 0.75em;
    grid-gap: 0;

    .favorite {
      .el-icon {
        font-size: 1.5em;
      }
    }

    .info {
      .options {
        justify-self: center;

        .optionsList {
          font-size: 0.75em;
        }
      }
    }
  }

  // decreasing font size for smaller screens
  @media (max-width: 500px) {
    grid-gap: 0;
    font-size: 0.65em;

    .favorite {
      .el-icon {
        font-size: 1.25em;
      }
    }

    .location-facility-type .location-facility-features .featuresIcons {
      .withIcon i {
        font-size: 1.6em;
      }
    }

    .info {
      .description {
        font-size: 1.4em;
      }

      .remaining,
      .packageInfo,
      .options {
        margin: 15px 0;

        // community service package text is too long
        .b {
          span {
            max-width: 223px;
          }
        }
      }
      .withIcon {
        text-wrap: wrap;
        white-space: normal;
      }
      .options {
        justify-self: center;
        h4 {
          font-size: 1.4em;
        }
        .optionsList {
          font-size: 0.8em;
        }
      }
    }
  }

  /* Styles for print */
  @media print {
    .hidden-on-print {
      display: none !important;
      /* or any other property to hide the element when printing */
    }

    grid-template-columns: auto;
    page-break-inside: avoid;
    box-shadow: none;
    border: solid 1px;
    border-radius: 5px;

    .location-title,
    .location-facility-type,
    .info,
    .withIcon,
    i {
      color: black !important;
    }

    // expand the features and show the comment element
    .location-facility-type {
      flex-direction: column;
      align-items: flex-start;
      .location-facility-features {
        margin: 0;

        .featuresIcons {
          flex-direction: column;
          gap: 0px;
          background: none;

          .withIcon {
            display: flex;
          }

          .expand,
          .collapse {
            display: block !important;
          }

          .optionsTitleInHeader {
            display: block !important;
          }
        }
      }
    }

    .info {
      grid-template-columns: 0em 30em 1fr;
      gap: 0em 2em;

      .location-image {
        display: none;
      }
      .packageInfo {
        margin-top: 0;
      }

      .description,
      .features {
        padding: 0 0 1em 0;
      }

      .options {
        box-shadow: none;
        label.el-checkbox + label.el-checkbox {
          border-top: 1px solid gray;
          padding-top: 0.5em;
        }

        // hide the original checkbox
        // (we needed to use different icons for print because the "checked" state of the element plus checkbox wasn't showing up)
        .el-checkbox__inner {
          display: none;
        }

        // set both the checkboxes to their unchecked state
        .optionsList .withIcon i {
          background: transparent;
          &.mdi-checkbox-marked-outline {
            display: none;
          }
          &.mdi-checkbox-blank-outline {
            display: inline-flex;
          }
        }

        // if the checkbox is checked, show the checked icon
        .is-checked {
          .optionsList .withIcon i {
            &.mdi-checkbox-marked-outline {
              display: inline-flex;
            }
            &.mdi-checkbox-blank-outline {
              display: none;
            }
          }
        }
      }
    }
  }
}
</style>
